// PLANNING-01C
// Página especializada de planificación completa de Seguimientos.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { backLabel } from '../../pricingJourney/navigation';


import { pageFrameSx } from '../../pricingJourney/components/PageHeader';

import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import ChevronRightRoundedIcon
  from '@mui/icons-material/ChevronRightRounded';

import MarginCenterSpecializedPageHeader
  from '../components/MarginCenterSpecializedPageHeader';

import {
  getMarginCenterFollowUpPlanning,
} from '../services/marginCenterService';

import type {
  MarginCenterFollowUpPlanningItem,
  MarginCenterFollowUpPlanningState,
} from '../types/followUpPlanning';
import { toSpanishNumber } from '../../../modules/pricingJourney/formatNumber';

const PAGE_SIZE = 25;

const STATES: MarginCenterFollowUpPlanningState[] = [
  'all',
  'due',
  'future',
];

function selected<T extends string>(
  raw: string | null,
  allowed: T[],
  fallback: T,
): T {
  return raw && allowed.includes(raw as T)
    ? raw as T
    : fallback;
}

function safeReturnPath(
  path: string | null,
): string {
  return path?.startsWith('/margin-center/')
    && !path.startsWith('//')
    ? path
    : '/margin-center/control-comercial?context=global';
}

function formatDateTime(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString(
      'es-ES',
      {
        dateStyle: 'medium',
        timeStyle: 'short',
      },
    );
}

function scopeLabel(
  value: string,
): string {
  if (value === 'customer') return 'Cliente';
  if (value === 'product') return 'Producto';
  if (value === 'customer_product') {
    return 'Relación cliente-producto';
  }
  if (value === 'transaction') return 'Transacción';
  return 'Seguimiento';
}

function stateLabel(
  value: MarginCenterFollowUpPlanningState,
): string {
  if (value === 'due') return 'Vencidas';
  if (value === 'future') return 'Futuras';
  return 'Todas';
}

function itemStateLabel(
  value: 'due' | 'future',
): string {
  return value === 'due'
    ? 'Vencida'
    : 'Programada';
}

export default function MarginCenterFollowUpPlanningPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const state =
    selected(
      params.get('state'),
      STATES,
      'all',
    );

  const cursor =
    params.get('cursor') ?? '';

  const backPath =
    safeReturnPath(
      params.get('returnTo'),
    );

  const [
    cursorTrail,
    setCursorTrail,
  ] = useState<string[]>([]);

  const planning = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'planning',
      state,
      PAGE_SIZE,
      cursor,
    ],
    queryFn: () =>
      getMarginCenterFollowUpPlanning({
        state,
        limit: PAGE_SIZE,
        cursor: cursor || undefined,
      }),
    staleTime: 20 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const changeState = (
    nextState: MarginCenterFollowUpPlanningState,
  ) => {
    const next =
      new URLSearchParams(params);

    next.set(
      'state',
      nextState,
    );

    next.delete('cursor');

    setCursorTrail([]);
    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const nextPage = () => {
    const nextCursor =
      planning.data?.page.next_cursor;

    if (!nextCursor) return;

    setCursorTrail((current) => [
      ...current,
      cursor,
    ]);

    const next =
      new URLSearchParams(params);

    next.set(
      'cursor',
      nextCursor,
    );

    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const previousPage = () => {
    if (cursorTrail.length === 0) {
      return;
    }

    const previous =
      cursorTrail[
        cursorTrail.length - 1
      ];

    setCursorTrail((current) =>
      current.slice(0, -1));

    const next =
      new URLSearchParams(params);

    if (previous) {
      next.set(
        'cursor',
        previous,
      );
    } else {
      next.delete('cursor');
    }

    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const firstPage = () => {
    const next =
      new URLSearchParams(params);

    next.delete('cursor');

    setCursorTrail([]);
    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const openFollowUp = (
    item: MarginCenterFollowUpPlanningItem,
  ) => {
    const planningReturnTo =
      `/margin-center/control-comercial/planificacion${
        params.toString()
          ? `?${params.toString()}`
          : ''
      }`;

    const next =
      new URLSearchParams();

    next.set(
      'context',
      'global',
    );

    next.set(
      'case',
      item.follow_up_id,
    );

    next.set(
      'caseReturnTo',
      planningReturnTo,
    );

    navigate(
      `/margin-center/control-comercial?${
        next.toString()
      }`,
    );
  };

  const scheduledCount =
    planning.data?.summary.scheduled_count
    ?? 0;

  const dueCount =
    planning.data?.summary.due_count
    ?? 0;

  const futureCount =
    planning.data?.summary.future_count
    ?? 0;

  const selectedCount =
    planning.data?.summary.selected_count
    ?? 0;

  const countFor = (
    candidate: MarginCenterFollowUpPlanningState,
  ) => {
    if (candidate === 'due') {
      return dueCount;
    }

    if (candidate === 'future') {
      return futureCount;
    }

    return scheduledCount;
  };

  const pageLabel =
    cursor
      ? cursorTrail.length > 0
        ? `Página ${cursorTrail.length + 1}`
        : 'Página actual'
      : 'Página 1';

  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={0.95}>
        <MarginCenterSpecializedPageHeader
          backLabel={backLabel(backPath)}
          onBack={() =>
            navigate(backPath)}
          eyebrow="Planificación comercial"
          title="Revisiones programadas"
          description="Agenda completa de Seguimientos abiertos. Las futuras son planificación; solo las vencidas constituyen atención vigente."
          meta={
            planning.data
              ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Actualizado ${
                    formatDateTime(
                      planning.data.generated_at,
                    )
                  }`}
                  sx={{
                    height: 24,
                    '& .MuiChip-label': {
                      px: 0.85,
                      fontSize: '0.75rem',
                      fontWeight: 750,
                    },
                  }}
                />
              )
              : undefined
          }
        />

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 0.9,
              md: 1,
            },
            border:
              '1px solid rgba(148,163,184,0.16)',
            bgcolor:
              'rgba(15,23,42,0.54)',
            borderRadius: 2.25,
          }}
        >
          <Stack spacing={1.1}>
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={0.75}
              alignItems={{
                xs: 'stretch',
                sm: 'center',
              }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={0.7}
                useFlexGap
                flexWrap="wrap"
              >
                {STATES.map(
                  (candidate) => (
                    <Button
                      key={candidate}
                      size="small"
                      variant={
                        state === candidate
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() =>
                        changeState(
                          candidate,
                        )}
                      sx={{
                        textTransform: 'none',
                      }}
                    >
                      {stateLabel(candidate)}
                      {' · '}
                      {
                        planning.isError
                          ? '—'
                          : toSpanishNumber(countFor(candidate))
                      }
                    </Button>
                  ),
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontVariantNumeric:
                      'tabular-nums',
                  }}
                >
                  {pageLabel}
                </Typography>

                <Chip
                  size="small"
                  variant="outlined"
                  label={`${
                    planning.data?.page.returned_count ?? 0
                  } de ${
                    toSpanishNumber(selectedCount)
                  }`}
                />
              </Stack>
            </Stack>

            {planning.isFetching && (
              <LinearProgress />
            )}

            {planning.isError && (
              <Alert
                severity="warning"
                action={(
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() =>
                      void planning.refetch()}
                  >
                    Reintentar
                  </Button>
                )}
              >
                No se pudo consultar la planificación completa.
              </Alert>
            )}

            {planning.data
              && planning.data.items.length === 0 && (
              <Alert severity="info">
                No hay revisiones en {
                  stateLabel(
                    state,
                  ).toLowerCase()
                }.
              </Alert>
            )}

            {planning.data
              && planning.data.items.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  border:
                    '1px solid rgba(148,163,184,0.13)',
                  borderRadius: 1.75,
                  bgcolor:
                    'rgba(2,6,23,0.20)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: {
                      xs: 'none',
                      lg: 'grid',
                    },
                    gridTemplateColumns:
                      '120px minmax(180px,1fr) minmax(150px,.85fr) minmax(230px,1.2fr) 170px 140px 24px',
                    gap: 2,
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.9,
                    bgcolor:
                      'rgba(15,23,42,0.88)',
                    borderBottom:
                      '1px solid rgba(148,163,184,0.12)',
                  }}
                >
                  {[
                    'Estado',
                    'Cliente',
                    'Producto',
                    'Próxima acción',
                    'Revisión',
                    'Actualizado',
                    '',
                  ].map((label) => (
                    <Typography
                      key={label || 'open'}
                      variant="caption"
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 800,
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                {planning.data.items.map(
                  (item, index) => (
                    <ButtonBase
                      key={item.planning_key}
                      onClick={() =>
                        openFollowUp(item)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderBottom:
                          index
                          < planning.data.items.length - 1
                            ? '1px solid rgba(148,163,184,0.10)'
                            : 'none',
                        '&:hover': {
                          bgcolor:
                            'rgba(56,189,248,0.055)',
                        },
                        '&:focus-visible': {
                          outline:
                            '2px solid #38bdf8',
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            lg:
                              '120px minmax(180px,1fr) minmax(150px,.85fr) minmax(230px,1.2fr) 170px 140px 24px',
                          },
                          gap: {
                            xs: 0.8,
                            lg: 2,
                          },
                          alignItems: 'center',
                          px: 1.5,
                          py: 1.25,
                        }}
                      >
                        <Stack
                          spacing={0.4}
                          alignItems="flex-start"
                        >
                          <Chip
                            size="small"
                            label={
                              itemStateLabel(
                                item.planning_state,
                              )
                            }
                            color={
                              item.planning_state === 'due'
                                ? 'warning'
                                : 'info'
                            }
                            variant="outlined"
                          />

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              lineHeight: 1.2,
                            }}
                          >
                            {scopeLabel(item.scope)}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 800,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: {
                              xs: 'normal',
                              lg: 'nowrap',
                            },
                          }}
                        >
                          {
                            item.customer_label
                            || item.customer_id
                            || 'Sin cliente'
                          }
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: {
                              xs: 'normal',
                              lg: 'nowrap',
                            },
                          }}
                        >
                          {
                            item.product_label
                            || item.product_id
                            || 'Sin producto'
                          }
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#cbd5e1',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {
                            item.next_action
                            || 'Pendiente de definir'
                          }
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              item.planning_state === 'due'
                                ? '#fbbf24'
                                : '#cbd5e1',
                            fontWeight:
                              item.planning_state === 'due'
                                ? 800
                                : 500,
                            fontVariantNumeric:
                              'tabular-nums',
                          }}
                        >
                          {
                            formatDateTime(
                              item.next_review_at,
                            )
                          }
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#94a3b8',
                            fontVariantNumeric:
                              'tabular-nums',
                          }}
                        >
                          {
                            formatDateTime(
                              item.updated_at,
                            )
                          }
                        </Typography>

                        <ChevronRightRoundedIcon
                          fontSize="small"
                          sx={{
                            color: '#64748b',
                            justifySelf: 'end',
                            display: {
                              xs: 'none',
                              lg: 'block',
                            },
                          }}
                        />
                      </Box>
                    </ButtonBase>
                  ),
                )}
              </Paper>
            )}

            {planning.data && (
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={0.75}
                alignItems={{
                  xs: 'stretch',
                  sm: 'center',
                }}
                justifyContent="space-between"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                  }}
                >
                  Mostrando {
                    planning.data.page.returned_count
                  } de {
                    toSpanishNumber(selectedCount)
                  } revisiones · {
                    stateLabel(state)
                  }
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  justifyContent="flex-end"
                >
                  {cursor
                    && cursorTrail.length === 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={firstPage}
                    >
                      Inicio
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      cursorTrail.length === 0
                    }
                    onClick={previousPage}
                  >
                    Anterior
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      !planning.data.page.has_more
                      || !planning.data.page.next_cursor
                    }
                    onClick={nextPage}
                  >
                    Siguiente
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
